import { useState } from 'react'

import { Box, Button, Href, Spinner, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  AdditionalCompensationsDocument,
  AdditionalCompensationsQuery,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesReferenceContractFragment,
  ContractsTaxonomiesDocument,
  TaskDetailAffecteeReferenceContractFragment,
} from 'generated/resources/graphql'
import { uniqueId } from 'lodash'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'

import { CurrencyContextProvider } from 'modules/compensations/lib/CurrencyContext'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import { useCanUpsellPayroll } from 'modules/contracts/hooks/useCanUpsellPayroll'
import { compensationDefaultAttributes } from 'modules/contracts/lib/additionalCompensations'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { translate } from 'modules/contracts/lib/translate'
import Compensation from 'modules/contracts/store/models/Compensation'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { ContractAdditionalCompensationList } from './ContractAdditionalCompensationList'
import { getFirstPaymentDate } from './CreateAdditionalCompensation/lib/getFirstPaymentDate'
import AdditionalCompensationsModal from './Modal'
import { DeprecatedAdditionalCompensation } from './_AdditionalCompensation'

interface AdditionalCompensationsHintProps {
  translations: { [key: string]: string }
  legalEntityId: number
  step: number
  type: ContractFlowType
}

const AdditionalCompensationsHint = ({
  translations,
  legalEntityId,
  step,
  type,
}: AdditionalCompensationsHintProps) => {
  const { navigation } = useNavigation()

  const trackLink = () => {
    track(trackingEvents.employee.contracts.goToAC, {
      step: step,
      type: type,
    })
  }

  return (
    <Hint color="grey200">
      <Href
        onClick={() => trackLink()}
        href={navigation.settings.legalEntitiesId.contracts.fullPath({ id: legalEntityId })}
        target="_blank"
      >
        <Text.Normal color="grey1000" weight="normal" size="100" underlined>
          {translate(translations, 'additional_compensations_hint')}
        </Text.Normal>
      </Href>
    </Hint>
  )
}

interface Props {
  translations: { [key: string]: string }
  step: number
  type: ContractFlowType
  isNewAdditionalCompensationsSupportEnabled?: boolean
  form?: Form
  legalEntityId: number
  hasPayrollBundle: boolean
  employeeLegalEntityCurrency: string
  contractVersion:
    | ContractsEmployeesReferenceContractFragment
    | ContractsEmployeesContractVersionFragment
    | TaskDetailAffecteeReferenceContractFragment
    | null
    | undefined
}

const AdditionalCompensations = ({
  translations,
  step,
  type,
  isNewAdditionalCompensationsSupportEnabled,
  form,
  legalEntityId,
  hasPayrollBundle,
  employeeLegalEntityCurrency,
  contractVersion,
}: Props) => {
  const {
    compensations,
    setCompensations,
    contractVersionPolicy,
    additionalCompensations: additionalCompensationsList,
    setAdditionalCompensations,
  } = useContractFlow()
  const { canUpsellPayroll } = useCanUpsellPayroll()
  const policies = usePolicies()
  const [showModal, setShowModal] = useState(false)

  const {
    company: { id: companyId },
    access: { locale },
  } = useCurrentSession()

  const {
    data: additionalCompensations,
    loading,
    error,
  } = useQuery(AdditionalCompensationsDocument, {
    variables: {
      contractVersionIds: contractVersion && contractVersion.id ? [contractVersion.id] : [],
      companyId: companyId,
    },
    onCompleted: (data: AdditionalCompensationsQuery) => {
      const additionalCompensationsNodes = data?.contracts.additionalCompensationsConnection.nodes

      if (
        (additionalCompensationsList === null || additionalCompensationsList === undefined) &&
        additionalCompensationsNodes !== undefined
      ) {
        const effectiveOn = form?.get('effective_on')?.value

        const compensationsWithFirstPaymentDate = additionalCompensationsNodes.map(
          (compensation) => {
            const effectiveAfterFirstPayment =
              effectiveOn &&
              compensation.firstPaymentOn &&
              stringToDate(effectiveOn, 'ui').isAfter(
                stringToDate(compensation.firstPaymentOn, 'backend')
              )

            if (effectiveOn && (!compensation.firstPaymentOn || effectiveAfterFirstPayment)) {
              return { ...compensation, firstPaymentOn: getFirstPaymentDate(effectiveOn) }
            }

            return compensation
          }
        )

        setAdditionalCompensations(compensationsWithFirstPaymentDate)
      }
    },
  })
  const defaultCompensation = new Compensation(
    compensationDefaultAttributes({
      contractVersionId: contractVersion?.id,
      policies,
      contractVersionPolicy,
      hasPayrollBundle,
    })
  )
  const [currentCompensation, setCurrentCompensation] = useState<Compensation | undefined>()
  const isCyclesManagementFeatureEnabled =
    additionalCompensations?.features?.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_compensations_cycles_management'
    )?.enabled ?? false

  const addCompensation = () => {
    setCurrentCompensation(defaultCompensation)
    setShowModal(true)
  }

  const editCompensation = (compensation: Compensation) => {
    setCurrentCompensation(compensation)
    setShowModal(true)
  }

  const onAdditionalCompensationsChange = (compensation: Compensation) => {
    let newCompensations = compensations ?? []
    if (compensation.attributes.temporal_id) {
      newCompensations = (compensations ?? []).map(function (c) {
        return c.attributes.temporal_id === compensation.attributes.temporal_id ? compensation : c
      })
    } else {
      compensation.attributes.temporal_id = uniqueId()
      newCompensations.push(compensation)
    }
    setCompensations(newCompensations)
  }

  const onRemoveCompensation = (compensation: Compensation) => {
    const withoutRemovedCompensation = (compensations ?? []).filter(
      (c) => c.attributes.temporal_id !== compensation.attributes.temporal_id
    )
    setCompensations(withoutRemovedCompensation)
  }

  const isCompensationEditable =
    type !== 'fix' ||
    currentCompensation?.isNew ||
    currentCompensation?.attributes.sync_with_supplements === false

  const { data } = useQuery(ContractsTaxonomiesDocument, {
    variables: {
      ids:
        compensations?.map((compensation) =>
          Number(compensation.attributes.contracts_taxonomy_id)
        ) ?? [],
    },
  })

  const disabledPayrollConceptIds =
    additionalCompensations?.compensations.payrollConceptStatusesConnection.nodes
      .filter((status) => !status.enabled)
      .map((status) => status.payrollConceptId)

  const filteredPayrollConcepts =
    additionalCompensations?.payroll.conceptsConnection.nodes.filter(
      (concept) =>
        concept.name !== DEFAULT_RESULT_CONCEPT.base_salary &&
        !disabledPayrollConceptIds?.includes(concept.id)
    ) || []

  if (error) throw new Error('Error fetching data')

  return (
    <>
      {showModal && currentCompensation && (
        <AdditionalCompensationsModal
          compensation={currentCompensation}
          employeeLegalEntityCurrency={employeeLegalEntityCurrency}
          onSubmit={(compensation: Compensation) => onAdditionalCompensationsChange(compensation)}
          onRemoveCompensation={(compensation: Compensation) => onRemoveCompensation(compensation)}
          isEditable={isCompensationEditable}
          onClose={() => setShowModal(false)}
        />
      )}
      <Box gap="s24">
        <Box gap={isNewAdditionalCompensationsSupportEnabled ? 's24' : 's8'}>
          <FormWrapper>
            <Text.Normal color="grey1000" weight="normal" size="200">
              {translate(translations, 'additional_compensations')}
            </Text.Normal>
          </FormWrapper>
          <FormWrapper
            hint={
              !isNewAdditionalCompensationsSupportEnabled ? (
                <AdditionalCompensationsHint
                  translations={translations}
                  legalEntityId={legalEntityId}
                  type={type}
                  step={step}
                />
              ) : (
                ''
              )
            }
          >
            {isNewAdditionalCompensationsSupportEnabled && additionalCompensations !== undefined ? (
              loading ? (
                <Box
                  gap="s8"
                  padding="s48"
                  alignItems="center"
                  data-testid="additional-compensations-loading"
                >
                  <Spinner size="s32" data-testid="loading" />
                </Box>
              ) : (
                <CurrencyContextProvider
                  defaultCurrency={
                    isCyclesManagementFeatureEnabled ? employeeLegalEntityCurrency : null
                  }
                >
                  <ContractAdditionalCompensationList
                    additionalCompensations={additionalCompensationsList ?? []}
                    type={type}
                    payrollConcepts={filteredPayrollConcepts}
                    effectiveOn={form?.get('effective_on')?.value || null}
                    contractVersionId={contractVersion?.id ?? 0}
                  />
                </CurrencyContextProvider>
              )
            ) : (
              compensations && (
                <>
                  {canUpsellPayroll && (
                    <UpsellingKit.ProductCard
                      moduleRequested={AvailableModules.compensations}
                      requestSource="contracts flow"
                      requestReason={ValidReasons.MissingAddon}
                    />
                  )}

                  <Box gap="s8">
                    {compensations.map((compensation) => {
                      const compensationTaxonomy = data?.contracts.taxonomies.find(
                        (taxonomy) => taxonomy.id === compensation.attributes.contracts_taxonomy_id
                      )
                      return (
                        <DeprecatedAdditionalCompensation
                          legalEntityCurrency={employeeLegalEntityCurrency}
                          locale={locale}
                          key={`compensation-${compensation.attributes.temporal_id}`}
                          onEdit={(comp: Compensation) => editCompensation(comp)}
                          compensation={compensation}
                          taxonomy={compensationTaxonomy}
                        />
                      )
                    })}
                  </Box>
                </>
              )
            )}
          </FormWrapper>
        </Box>
        {!isNewAdditionalCompensationsSupportEnabled && (
          <FormWrapper>
            <Button onClick={() => addCompensation()} iconName="add">
              {translate(translations, 'add_compensation')}
            </Button>
          </FormWrapper>
        )}
      </Box>
    </>
  )
}

export default observer(AdditionalCompensations)
